import React, { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Pagination, Space, Spin } from "antd"
import { ColumnsType } from "antd/lib/table"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { colorLightGrey } from "../../styles/colors"
import { AppSettings } from "../../layouts/Main/MainLayout"
import CourseCard, { CourseCardItem } from "./CourseCard"
import CourseTableLegend from "./CourseTableLegend"
import css from "./CourseTable.module.css"

type CourseGridProps = {
    items: ReadonlyArray<CourseCardItem> | undefined
    // Weitere Kacheln hinter den Kursen im selben Raster (Interessenten-Listen).
    extraCards?: ReadonlyArray<React.ReactNode>
    loading: boolean
    total: number | undefined
    columnsToUse: ColumnsType<CourseCardItem>
    pageSize: number
    maxColumns: number
    gap: number
    currentPage: number
    onPaginationChange: (page: number, pageSize: number) => void
    showLegendRestrictions: boolean
    showLegendMemberDiscount: boolean
    showLegendStatus: boolean
    appSettings: AppSettings
    // Leertext inkl. Hinweis auf versteckte Kurse (Issue #769); ohne Prop der bisherige Text.
    emptyText?: React.ReactNode
}

const CourseGrid: FC<CourseGridProps> = ({
    items,
    extraCards = [],
    loading,
    total,
    columnsToUse,
    pageSize,
    maxColumns,
    gap,
    currentPage,
    onPaginationChange,
    showLegendRestrictions,
    showLegendMemberDiscount,
    showLegendStatus,
    appSettings,
    emptyText,
}) => {
    const { media } = useMediaSize()
    const { t } = useTranslation()

    // Effektive Spaltenanzahl: respektiert das Setting als Obergrenze, reduziert auf
    // schmaleren Bildschirmen automatisch. Mobile (xs) wird vom Caller nicht erreicht
    // (dort greift die existierende Card-List).
    const effectiveColumns = useMemo(() => {
        const safeMax = Math.max(1, Math.min(maxColumns, 6))
        if (media === "xs") return 1
        if (media === "sm") return Math.min(safeMax, 1)
        if (media === "md") return Math.min(safeMax, 2)
        if (media === "lg") return Math.min(safeMax, 3)
        return safeMax
    }, [media, maxColumns])

    const showPagination = (total ?? 0) > pageSize

    return (
        <div className={css.courseGridContainer} data-testid='course-grid'>
            {loading && (!items || items.length === 0) ? (
                <Space style={{ minHeight: 100, display: "flex", justifyContent: "center", padding: 30 }}>
                    <Spin />
                </Space>
            ) : (items?.length ?? 0) === 0 && extraCards.length === 0 ? (
                <Space style={{ minHeight: 100, display: "flex", justifyContent: "center" }}>
                    {emptyText ?? (
                        <h3 style={{ color: colorLightGrey }}>
                            {t("courseFilters.empty.title", "Für diese Auswahl stehen derzeit keine Kurse zur Verfügung.")}
                        </h3>
                    )}
                </Space>
            ) : (
                <div
                    className={css.courseGrid}
                    style={{
                        gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
                        gap: `${Math.max(0, Math.min(gap, 48))}px`,
                        opacity: loading ? 0.6 : 1,
                        transition: "opacity 150ms ease",
                    }}
                >
                    {items?.map((course) => (
                        <CourseCard key={course.id} id={`course-${course.id}`} course={course} columnsToUse={columnsToUse} className={css.courseGridCard} />
                    ))}
                    {extraCards}
                </div>
            )}

            {showPagination && (
                <div className={css.courseGridPagination}>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={total}
                        showSizeChanger={false}
                        hideOnSinglePage
                        showTotal={(count) => `Gesamtanzahl ${count}`}
                        simple={media === "xs" || media === "sm"}
                        responsive
                        onChange={onPaginationChange}
                    />
                </div>
            )}

            <div className={css.courseGridFooter}>
                {(showLegendStatus || showLegendRestrictions) && (
                    <CourseTableLegend
                        showStatus={showLegendStatus}
                        showRestrictions={showLegendRestrictions}
                        showMemberDiscount={showLegendMemberDiscount}
                        appSettings={appSettings}
                    />
                )}
            </div>
        </div>
    )
}

export default CourseGrid
